import React, { useState } from "react";
import ChartStatusStrip from "../components/dashboard/ChartStatusStrip";
import ChartTrustIndicator from "../components/dashboard/ChartTrustIndicator";
import TimeframeStrip from "../components/dashboard/TimeframeStrip";
import DecisionLane from "../components/dashboard/DecisionLane";
import CandlestickChart from "../components/CandlestickChart";

/** Local-only visual harness. Renders the real chart chrome components. */

const now = Date.now();
const at = (m) => new Date(now - m * 60000).toISOString();

const DECISIONS = [
  { id: "1", at: at(300), source: "automation", kind: "wait", symbol: "MNQ", reason_code: "WAITING_FOR_NEXT_SIGNAL_TRANSITION", reason: "Strategy is BUY but the transition already occurred." },
  { id: "2", at: at(262), source: "ai", kind: "wait", symbol: "MNQ", action: "WAIT", reason_code: "AI_RESEARCH_WAIT", rationale: "No supplied market presents a defensible intraday setup." },
  { id: "3", at: at(231), source: "ai", kind: "blocked", symbol: "MCL", action: "ENTER_SHORT", reason_code: "AI_RESEARCH_EXECUTION_BLOCKED", reason: "Limit price sits through the market." },
  { id: "4", at: at(184), source: "automation", kind: "entry", symbol: "MNQ", action: "BUY", reason_code: "SIGNAL_ACCEPTED" },
  { id: "5", at: at(151), source: "automation", kind: "hold", symbol: "MNQ", reason_code: "ALREADY_IN_POSITION" },
  { id: "6", at: at(122), source: "ai", kind: "wait", symbol: "MNQ", action: "WAIT", reason_code: "AI_RESEARCH_WAIT" },
  { id: "7", at: at(93), source: "automation", kind: "exit", symbol: "MNQ", action: "SELL", reason_code: "STOP_LOSS" },
  { id: "8", at: at(61), source: "ai", kind: "blocked", symbol: "MGC", reason_code: "AI_RESEARCH_EXECUTION_BLOCKED" },
  { id: "9", at: at(34), source: "automation", kind: "error", symbol: "MNQ", reason_code: "BROKER_STATE_UNKNOWN" },
  { id: "10", at: at(9), source: "ai", kind: "wait", symbol: "MNQ", action: "WAIT", reason_code: "AI_RESEARCH_WAIT" },
];

const LADDER = [
  { tf: "1d", n: 15, c: 7444, hi: 7632, lo: 7411.75, chg: -83.25, pos: 0.146, chg_atr: -1.15, trend: "down" },
  { tf: "1h", n: 30, c: 7444, hi: 7496.5, lo: 7411.75, chg: 0.5, pos: 0.381, chg_atr: 0.02, trend: "up" },
  { tf: "30m", n: 30, c: 7444, hi: 7496.5, lo: 7431.25, chg: 2.5, pos: 0.195, chg_atr: 0.13, trend: "down" },
  { tf: "15m", n: 40, c: 7444, hi: 7496.5, lo: 7431.25, chg: -16.75, pos: 0.195, chg_atr: -1.68, trend: "down" },
  { tf: "5m", n: 48, c: 7444, hi: 7480.75, lo: 7432, chg: -26.75, pos: 0.246, chg_atr: -5.41, trend: "down" },
  { tf: "2m", n: 45, c: 7444, hi: 7450.5, lo: 7435.5, chg: 5.25, pos: 0.567, chg_atr: 2.41, trend: "up" },
];

const SCENARIOS = {
  unprotected: {
    label: "Open position, NO broker stop",
    status: { accountName: "50KTC-V2-30451-24103673", accountActive: true, accountCanTrade: true, brokerConnected: true, brokerStateKnown: true, symbol: "MNQ", contractLabel: "MNQU26", strategyName: "KAMA Middle Regime", strategyTimeframe: 15, chartTimeframe: "5m", marketLive: true, marketQuality: "Live", autoTradeEnabled: true, inAutomationScope: true, hasActiveLink: true, hasOpenPosition: true, unsafePosition: true, protectionConfirmed: false, latestDecision: { decision: "wait", reason_code: "AI_RESEARCH_WAIT" } },
    trust: { source: "topstepx", sourceLabel: "TopstepX", lastBarAt: new Date().toISOString() },
  },
  healthy: {
    label: "Live, protected position",
    status: { accountName: "PRAC-V2-30451-60402414", accountActive: true, accountCanTrade: true, brokerConnected: true, brokerStateKnown: true, symbol: "MNQ", contractLabel: "MNQU26", strategyName: "KAMA Middle Regime", strategyTimeframe: 15, chartTimeframe: "15m", marketLive: true, marketQuality: "Live", autoTradeEnabled: true, inAutomationScope: true, hasActiveLink: true, hasOpenPosition: true, protectionConfirmed: true, latestDecision: { decision: "execute", reason_code: "SIGNAL_ACCEPTED" } },
    trust: { source: "topstepx", sourceLabel: "TopstepX", lastBarAt: new Date().toISOString() },
  },
  disconnected: {
    label: "No account connected",
    status: { accountName: null, brokerConnected: false, brokerStateKnown: false, symbol: "ES", chartTimeframe: "5m", marketQuality: "Historical stale", hasActiveLink: false },
    trust: { source: "historical_store", sourceLabel: "Historical Store", lastBarAt: at(600) },
  },
};

const CANDLES = (() => {
  const out = [];
  let price = 7440;
  const start = Date.now() - 300 * 60000;
  for (let i = 0; i < 300; i += 1) {
    const open = price;
    const move = (Math.sin(i / 9) + (i % 7 === 0 ? 0.8 : -0.3)) * 1.6;
    const close = open + move;
    out.push({
      timestamp: new Date(start + i * 60000).toISOString(),
      open, high: Math.max(open, close) + 0.9, low: Math.min(open, close) - 0.9, close,
      volume: 120 + (i % 23) * 9,
    });
    price = close;
  }
  return out;
})();

const Fake = ({ label }) => (
  <div className="flex h-full items-center justify-center rounded-md border border-white/10 bg-gradient-to-b from-white/[0.03] to-transparent text-xs text-white/25">
    {label}
  </div>
);

const ChartChromePreview = () => {
  const initial = new URLSearchParams(window.location.search).get("s");
  const [key, setKey] = useState(SCENARIOS[initial] ? initial : "unprotected");
  const s = SCENARIOS[key];
  return (
    <div className="min-h-screen bg-[#0a0a0b] p-3 text-white">
      <div className="mx-auto max-w-[1360px] space-y-2">
        <div className="flex flex-wrap items-center gap-2 pb-1">
          <span className="text-[10px] uppercase tracking-widest text-white/35">Chart chrome — real components</span>
          {Object.entries(SCENARIOS).map(([id, v]) => (
            <button key={id} type="button" onClick={() => setKey(id)}
              className={`rounded-md border px-2 py-1 text-[11px] ${key === id ? "border-blue-400/60 bg-blue-500/20 text-blue-100" : "border-white/10 bg-white/5 text-white/60"}`}>
              {v.label}
            </button>
          ))}
        </div>

        <ChartStatusStrip {...s.status} />

        <div className="flex flex-wrap items-center gap-2 rounded-md border border-white/10 bg-black/20 px-2 py-1.5">
          <span className="text-xs font-semibold text-white">{s.status.symbol}</span>
          <span className="text-[11px] text-white/40">{s.status.chartTimeframe}</span>
          <span className="text-[11px] text-white/40">Vol · SMA20 · EMA21</span>
          <span className="text-[11px] text-white/40">{s.status.strategyName || "No strategy overlay"}</span>
          <div className="ml-auto flex items-center gap-2">
            <ChartTrustIndicator {...s.trust} dataQualityLabel="Verified" paritySignature="2e922360" />
            <span className="text-[10px] text-white/30">300 bars</span>
          </div>
        </div>

        <TimeframeStrip symbol={s.status.symbol} timeframes={LADDER}
          priorDay={{ prior_day_high: 7550, prior_day_low: 7411.75, prior_day_close: 7449 }}
          activeTimeframe={s.status.chartTimeframe}
          selectableTimeframes={["1m", "2m", "5m", "15m", "1h", "4h", "Day"]} onSelectTimeframe={() => {}} />

        <div className="flex-1 min-h-0" style={{ height: "42vh" }}>
          <CandlestickChart
            candles={CANDLES}
            symbol={s.status.symbol}
            showVolume
            height="100%"
            width="100%"
          />
        </div>

        <DecisionLane decisions={DECISIONS}
          counts={{ wait: 4, blocked: 2, entry: 1, exit: 1, hold: 1, error: 1 }} windowHours={24} />
      </div>
    </div>
  );
};

export default ChartChromePreview;
